import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  Box,
  Grow,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { appsLauncherItems } from "../../config/appsLauncherConfig";
import { dash } from "../dashboard/dashboardTokens";

const ICONS = {
  storefront: StorefrontOutlinedIcon,
};

function usePrefersDarkMode() {
  const theme = useTheme();
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== "undefined"
      && window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
  );

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return undefined;
    const handler = (e) => setSystemDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return theme.palette.mode === "dark" || systemDark;
}

export default function AppsLauncher({
  apps = appsLauncherItems,
  ariaLabel = "Open WappGrow apps",
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = usePrefersDarkMode();
  const titleId = useId();
  const buttonRef = useRef(null);
  const itemRefs = useRef([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const open = Boolean(anchorEl);

  const surface = isDark ? theme.palette.grey[900] : "#2e10c0";
  const textPrimary = isDark ? theme.palette.grey[100] : "#0f172a";
  const textMuted = isDark ? theme.palette.grey[400] : dash.textMuted;
  const border = isDark ? alpha(theme.palette.common.white, 0.12) : dash.border;
  const hoverBg = isDark ? alpha(theme.palette.common.white, 0.06) : dash.accentSoft;
  const iconBg = isDark ? alpha(theme.palette.primary.main, 0.2) : alpha(dash.accent, 0.1);

  const close = useCallback(() => {
    setAnchorEl(null);
    setFocusedIndex(-1);
    buttonRef.current?.focus();
  }, []);

  const openLauncher = (event) => {
    setAnchorEl(event.currentTarget);
    setFocusedIndex(0);
  };

  const launchApp = (app) => {
    if (app.route) {
      navigate(app.route);
    } else if (app.href) {
      if (app.openInNewTab) {
        window.open(app.href, "_blank", "noopener,noreferrer");
      } else {
        window.location.assign(app.href);
      }
    }
    close();
  };

  useEffect(() => {
    if (!open || focusedIndex < 0) return;
    itemRefs.current[focusedIndex]?.focus();
  }, [open, focusedIndex]);

  const handleLauncherKeyDown = (event) => {
    if (!open) return;
    const count = apps.length;
    if (!count) return;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((i) => (i + 1) % count);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((i) => (i - 1 + count) % count);
        break;
      case "Home":
        event.preventDefault();
        setFocusedIndex(0);
        break;
      case "End":
        event.preventDefault();
        setFocusedIndex(count - 1);
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={openLauncher}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? titleId : undefined}
        size="medium"
        sx={{
          color: isDark ? theme.palette.grey[600] : "var(--theme-app-bar-text, #334155)",
          borderRadius: 2,
          transition: `background-color ${dash.transition}, color ${dash.transition}, transform ${dash.transition}`,
          "&:hover": {
            bgcolor: hoverBg,
            color: dash.accent,
          },
          "&:focus-visible": {
            outline: `2px solid ${dash.accent}`,
            outlineOffset: 2,
          },
        }}
      >
        <AppsIcon />
      </IconButton>

      <Popover
        id={titleId}
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        marginThreshold={12}
        TransitionComponent={Grow}
        transitionDuration={{ enter: 200, exit: 160 }}
        TransitionProps={{
          onEntered: () => itemRefs.current[0]?.focus(),
        }}
        slotProps={{
          paper: {
            onKeyDown: handleLauncherKeyDown,
            sx: {
              mt: 1,
              width: { xs: "min(92vw, 340px)", sm: 360 },
              p: { xs: 1.5, sm: 2 },
              borderRadius: `${dash.radiusLg * 4}px`,
              bgcolor: "#06213e",
              border: `1px solid ${border}`,
              boxShadow: isDark
                ? "0 16px 48px rgba(0,0,0,0.45)"
                : dash.shadowMd,
              overflow: "hidden",
              transformOrigin: "top right",
            },
          },
        }}
      >
        <Typography
          component="h2"
          sx={{
            px: 0.5,
            pb: 1.5,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: textMuted,
          }}
        >
          Estore Merchant 
        </Typography>

        <Box
          role="list"
          sx={{
            display: "grid",
            gridTemplateColumns:
              apps.length === 1
                ? "1fr"
                : { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1,
          }}
        >
          {apps.map((app, index) => {
            const Icon = ICONS[app.iconKey] || StorefrontOutlinedIcon;
            return (
              <Box
                key={app.id}
                component="button"
                type="button"
                role="listitem"
                ref={(el) => { itemRefs.current[index] = el; }}
                onClick={() => launchApp(app)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    launchApp(app);
                  }
                }}
                aria-label={`${app.name}. ${app.description}`}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  width: "100%",
                  m: 0,
                  p: 1.25,
                  textAlign: "left",
                  cursor: "pointer",
                  border: `1px solid ${border}`,
                  borderRadius: 2,
                  bgcolor: "transparent",
                  color: textPrimary,
                  font: "inherit",
                  transition: `background-color ${dash.transition}, border-color ${dash.transition}, transform ${dash.transition}, box-shadow ${dash.transition}`,
                  "&:hover": {
                    bgcolor: hoverBg,
                    borderColor: alpha(dash.accent, 0.25),
                    transform: "translateY(-1px)",
                    boxShadow: isDark ? "none" : dash.shadowSm,
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${dash.accent}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    bgcolor: iconBg,
                    color: dash.accent,
                  }}
                >
                  <Icon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
                    {app.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: textMuted, mt: 0.35, lineHeight: 1.45 }}>
                    {app.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
}
